import type { PublicAnnouncement } from "@/features/announce/api/announceClient";
import { announcementTimestampEpoch } from "@/features/announce/lib/announcementReadState";

export function getAnnouncementsReadReceiptVersion(
  isSuccess: boolean,
  isMetaReady: boolean,
  isFetching: boolean,
  isStale: boolean,
  dataUpdatedAt: number,
  lastMarkedDataUpdatedAt: number,
): number | null {
  return isSuccess &&
    isMetaReady &&
    !isFetching &&
    !isStale &&
    dataUpdatedAt > 0 &&
    dataUpdatedAt > lastMarkedDataUpdatedAt
    ? dataUpdatedAt
    : null;
}

export function areAllFetchedAnnouncementsRead(
  announcements:
    | readonly Pick<
        PublicAnnouncement,
        "id" | "published_at" | "article_unread_revision"
      >[]
    | undefined,
  openedAnnouncementId: number,
  isAnnouncementRead: (
    announcement: Pick<
      PublicAnnouncement,
      "id" | "published_at" | "article_unread_revision"
    >,
  ) => boolean,
): boolean {
  return (
    announcements?.every(
      (announcement) =>
        announcement.id === openedAnnouncementId ||
        isAnnouncementRead(announcement),
    ) ?? false
  );
}

export function getAnnouncementReadThroughTimestamp(
  announcements: readonly Pick<
    PublicAnnouncement,
    "pinned_order" | "published_at"
  >[],
  openedAnnouncement: Pick<PublicAnnouncement, "pinned_order" | "published_at">,
): string | null {
  const candidates =
    openedAnnouncement.pinned_order === 1
      ? announcements
      : [...announcements, openedAnnouncement];
  let latest: string | null = null;
  let latestEpoch = Number.NEGATIVE_INFINITY;
  for (const announcement of candidates) {
    const epoch = announcementTimestampEpoch(announcement.published_at);
    if (epoch !== null && epoch > latestEpoch) {
      latest = announcement.published_at;
      latestEpoch = epoch;
    }
  }
  return latest;
}

export function getFetchedAnnouncementsReadThroughTimestamp(
  announcements: readonly Pick<PublicAnnouncement, "published_at">[],
): string | null {
  let latest: string | null = null;
  let latestEpoch = Number.NEGATIVE_INFINITY;
  for (const announcement of announcements) {
    const epoch = announcementTimestampEpoch(announcement.published_at);
    if (epoch !== null && epoch > latestEpoch) {
      latest = announcement.published_at;
      latestEpoch = epoch;
    }
  }
  return latest;
}

export function getAnnouncementReadThroughRevision(
  announcements: readonly Pick<
    PublicAnnouncement,
    "pinned_order" | "article_unread_revision"
  >[],
  openedAnnouncement: Pick<
    PublicAnnouncement,
    "pinned_order" | "article_unread_revision"
  >,
  snapshotRevision: number | null = null,
): number {
  const candidates =
    openedAnnouncement.pinned_order === 1
      ? announcements
      : [...announcements, openedAnnouncement];
  return candidates.reduce(
    (latest, announcement) =>
      Math.max(latest, announcement.article_unread_revision ?? 0),
    snapshotRevision ?? 0,
  );
}

export function getFetchedAnnouncementsReadThroughRevision(
  announcements: readonly Pick<PublicAnnouncement, "article_unread_revision">[],
  snapshotRevision: number | null = null,
): number {
  return announcements.reduce(
    (latest, announcement) =>
      Math.max(latest, announcement.article_unread_revision ?? 0),
    snapshotRevision ?? 0,
  );
}
